import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight, Phone, Mail } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/method", label: "Method" },
  { to: "/services", label: "Services" },
  { to: "/resources", label: "Resources" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-canvas/85 backdrop-blur-xl border-b border-hairline" : "bg-transparent"
      }`}
    >
      <div className="container-pad flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_ndis-compliance-pro/artifacts/ujy7rfap_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2009_05_32%20PM.png"
            alt="Elevate Consulting"
            className="h-12 w-12 object-contain"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif-mark text-navy text-xl tracking-tight">Elevate</div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-muted2 -mt-0.5">Consulting</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-navy" : "text-muted2 hover:text-navy"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/login"
            data-testid="client-portal-cta"
            className="text-sm font-medium text-navy hover:text-teal transition-colors"
          >
            Client Portal
          </Link>
          <Link
            to="/contact"
            data-testid="header-book-cta"
            className="btn-ochre text-sm"
          >
            Book free call <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" aria-label="Open menu" className="p-2 text-navy">
                <Menu size={24} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] sm:w-96 bg-canvas">
              <div className="flex flex-col gap-2 mt-8">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `px-2 py-3 text-lg font-display ${isActive ? "text-navy" : "text-muted2"}`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                <div className="border-t border-hairline mt-4 pt-4 flex flex-col gap-3">
                  <Link to="/login" data-testid="mobile-portal" className="btn-ghost-navy justify-center">
                    Client Portal
                  </Link>
                  <Link to="/contact" data-testid="mobile-book-cta" className="btn-ochre justify-center">
                    Book free call
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy text-canvas mt-32">
      <div className="container-pad py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_ndis-compliance-pro/artifacts/ujy7rfap_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2009_05_32%20PM.png"
              alt="Elevate Consulting"
              className="h-14 w-14 object-contain bg-canvas p-1"
            />
            <div>
              <div className="font-serif-mark text-2xl">Elevate</div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-canvas/60 -mt-0.5">Consulting</div>
            </div>
          </div>
          <h3 className="font-display text-2xl mt-8 max-w-md leading-snug">
            Elevating providers · Simplifying compliance · Empowering futures.
          </h3>
          <p className="text-canvas/70 mt-4 max-w-md text-sm leading-relaxed">
            Australia's specialist partner for NDIS registration and ongoing compliance.
          </p>
          <div className="mt-8 flex flex-col gap-3 text-canvas/80 text-sm">
            <a href="mailto:Jordan@elevateconsultingau.com" data-testid="footer-email" className="inline-flex items-center gap-2 hover:text-teal-400 transition-colors">
              <Mail size={16} /> Jordan@elevateconsultingau.com
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="overline">Explore</div>
          <ul className="mt-4 space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} data-testid={`footer-nav-${n.label.toLowerCase()}`} className="text-canvas/80 hover:text-teal-400 transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="overline">Legal</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/privacy" data-testid="footer-privacy" className="text-canvas/80 hover:text-teal-400 transition-colors">Privacy</Link></li>
            <li><Link to="/terms" data-testid="footer-terms" className="text-canvas/80 hover:text-teal-400 transition-colors">Terms</Link></li>
            <li><Link to="/login" data-testid="footer-portal" className="text-canvas/80 hover:text-teal-400 transition-colors">Client portal</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="overline">Locations</div>
          <p className="mt-4 text-sm text-canvas/80 leading-relaxed">
            Headquartered in NSW. <br />Servicing providers Australia wide.
          </p>
        </div>
      </div>
      <div className="border-t border-canvas/10">
        <div className="container-pad py-6 flex flex-col sm:flex-row gap-4 justify-between text-xs text-canvas/60">
          <div>© {new Date().getFullYear()} Elevate Consulting Pty Ltd. All rights reserved.</div>
          <div>ABN placeholder · We acknowledge the Traditional Custodians of the lands we work on.</div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
